import {api, ApiError} from './api.js';
import {apiUrl} from './config.js';

export type AuthUser = {id: string; email: string; phone: string; avatarDataUrl?: string; displayName: string; accountType?: 'personal'|'enterprise'; status: string; emailVerified: boolean; createdAt?: string};
export type AuthOrganization = {id: string; name: string; organizationType?: 'personal'|'team'; organizationCode?: string; teamPublicId?: string; ownerUserId: string; role?: string};
export type AuthSnapshot = {user: AuthUser; organization: AuthOrganization; role: string; sessionId?: string};

async function completeAuth(value: (AuthSnapshot & {ok?: boolean})|null): Promise<AuthSnapshot|null> {
  if (!value) return null;
  if (value.organization?.id) return value;
  const organizations = await api.get<{organizations:Array<AuthOrganization & {role?: string}>}>(apiUrl('/api/organizations'));
  const first = organizations.organizations?.[0];
  return first ? {...value, organization: first, role: value.role || first.role || 'member'} : value;
}

export async function currentAuth(): Promise<AuthSnapshot | null> {
  try { return await completeAuth(await api.get<AuthSnapshot & {ok: boolean}>(apiUrl('/api/auth/me'))); }
  catch (error) { if (error instanceof ApiError && error.status === 401) return null; throw error; }
}

export async function sendRegisterCode(email: string): Promise<{devCode?: string; message?: string}> {
  return api.post(apiUrl('/api/auth/send-code'), {email, purpose: 'register'});
}

export async function searchRegisterEnterprises(keyword: string): Promise<{configured: boolean; results: Array<{name: string; creditCode: string; status: string; address: string}>}> {
  return api.get(apiUrl(`/api/auth/enterprise-search?keyword=${encodeURIComponent(keyword)}`));
}

export async function register(email: string, phone: string, password: string, displayName: string, code: string, userType: 'personal'|'enterprise' = 'personal', enterpriseName = '', organizationCode = ''): Promise<AuthSnapshot> {
  return api.post(apiUrl('/api/auth/register'), {email, phone, password, displayName, code, userType, enterpriseName, organizationCode});
}

export async function login(email: string, password: string, rememberMe = false): Promise<AuthSnapshot> {
  return (await completeAuth(await api.post<AuthSnapshot & {ok?: boolean}>(apiUrl('/api/auth/login'), {email, password, rememberMe}))) as AuthSnapshot;
}

export async function logout(): Promise<void> {
  await api.post(apiUrl('/api/auth/logout'));
}

export async function sendResetCode(email: string): Promise<{devCode?: string; message?: string}> {
  return api.post(apiUrl('/api/auth/forgot-password'), {email});
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  await api.post(apiUrl('/api/auth/reset-password'), {email, code, newPassword});
}

export async function updateProfile(displayName: string, phone = '', avatarDataUrl = ''): Promise<AuthUser> {
  const result = await api.patch<{user: AuthUser}>(apiUrl('/api/auth/profile'), {displayName, phone, avatarDataUrl});
  return result.user;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post(apiUrl('/api/auth/change-password'), {currentPassword, newPassword});
}

/**
 * 注销当前账号，必须提交登录密码。
 * @param password 当前登录密码
 */
export async function deactivateAccount(password: string): Promise<void> {
  await api.delete(apiUrl('/api/auth/account'), {password});
}
